import {
  ActionIcon,
  type ComboboxData,
  Group,
  TextInput,
  Tooltip
} from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { IconSolarPanel2, IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useManagerVariant } from "../../../../hooks/useManagerVariant";
import { usePersons } from "../../../../hooks/usePersons";
import { FormField } from "../../../BuildingBlocks/Forms/MantineFormFields/FormField";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { type PVPlantWithMeter } from "../../SiteSetupAssistant.types";

interface PlantWithoutSEEProps {
  pvPlants: Array<PVPlantWithMeter>;
  index: number;
  error?: string;
  onChange: (data: Array<PVPlantWithMeter>, index: number) => void;
  onDelete: (index: number) => void;
}

function PlantWithoutSEE({
  pvPlants,
  index,
  error,
  onChange,
  onDelete
}: PlantWithoutSEEProps) {
  const { projectId } = useParams();
  const { variantId } = useManagerVariant(projectId);

  const {
    persons,
    error: companiesError,
    isLoading
  } = usePersons({ variantId: variantId ?? undefined });

  const [operatorOptions, setOperatorOptions] = useState<ComboboxData>([]);

  useEffect(() => {
    if (!isLoading && !error) {
      const usedOperatorNames = pvPlants
        .filter((pv) => pv.operator && pv.operator.name)
        .map((pv) => pv.operator?.name || "unbekannt");
      const uniqueOperatorOptions = Array.from(
        new Set(
          persons.map((person) => person.name).concat(...usedOperatorNames)
        )
      ).map((name) => ({ value: name, label: name }));

      setOperatorOptions(uniqueOperatorOptions);
    }
  }, [isLoading, error, persons, pvPlants]);

  const pvPlant = pvPlants[index];

  function handleChangeName(index: number, value: string) {
    const updatedPvPlants = pvPlants.map((pvPlant, idx) => {
      if (idx === index) {
        return {
          ...pvPlant,
          name: value || null,
          meter: { ...pvPlant.meter, name: "Zähler " + value }
        };
      }
      return pvPlant;
    });
    onChange(updatedPvPlants, index);
  }

  function handleCreateOperator(index: number, value: string | null) {
    if (!value) {
      return;
    }
    setOperatorOptions(operatorOptions.concat({ value, label: value }));
    handleChangeOperator(index, value);
  }

  function handleChangeOperator(index: number, value: string | number | null) {
    const updatedPvPlants = pvPlants.map((pvPlant, idx) => {
      if (idx === index && typeof value === "string") {
        return {
          ...pvPlant,
          operator: {
            name: value
          }
        };
      }
      return pvPlant;
    });
    onChange(updatedPvPlants, index);
  }

  if (!variantId) {
    console.error("A variant ID is required for PlantWithoutSEE component.");
    Sentry.captureException(
      new Error(
        "A variant ID is required for PlantWithoutSEE component at " +
          window.location.href
      )
    );
    return null;
  }

  return (
    <Group align="end" className="PlantWithoutSEE  single-plant" gap="sm">
      <LoadOrError error={companiesError} loading={isLoading}>
        <Group pb={error ? "xl" : "md"}>
          <IconSolarPanel2 />
        </Group>
        <TextInput
          error={error}
          label="Bezeichnung der Anlage"
          value={pvPlant.name || ""}
          onChange={(event) => handleChangeName(index, event.target.value)}
        />
        <FormField
          data={operatorOptions}
          error={error ? { message: error, type: "value" } : undefined}
          label="Name der Betreibergesellschaft"
          name="operator"
          type="select-with-create"
          value={pvPlant.operator?.name ?? undefined}
          onChange={(value) => handleChangeOperator(index, value)}
          onCreate={(value) => handleCreateOperator(index, value)}
        />
        <Tooltip label="Anlage löschen">
          <ActionIcon
            color="error"
            mb={error ? "xl" : 6}
            variant="transparent"
            onClick={() => onDelete(index)}
          >
            <IconTrash size="1.3rem" />
          </ActionIcon>
        </Tooltip>
      </LoadOrError>
    </Group>
  );
}

export { PlantWithoutSEE };
